/**
 * useLayoutSaveQueue — builder-v1 coalesced autosave for the custom-dashboard grid,
 * replacing the per-drag PUT storm (one immediate write per moved/resized event).
 *
 * Behaviour (spec §5 "Save model", plan step 13):
 *   - Trailing coalesce: every drag/resize (re)arms a ~1s timer; one settle flushes ALL
 *     pending coordinate changes at once.
 *   - Diff the WHOLE layout, never just the dragged widget — vertical-compact repacks
 *     neighbours, so a single drag can move several widgets; each changed widget gets one
 *     write (there is NO batch endpoint — verified W0.4).
 *   - Single serialized write chain: at most one write in flight, FIFO, so no earlier
 *     write can land after a later one. Ordering/correctness come from serialization —
 *     the generation token only lets a stale RESPONSE be ignored, it can never recall a
 *     PUT already sent (spec §5).
 *   - Every write carries CAPTURED context (facility captured when the change was made),
 *     so a flush that lands after a facility switch still targets the edited dashboard.
 *
 * Factory, not a module singleton: the queue owns per-session state (baseline, timer,
 * status). One CustomDashboard instance creates one queue. State is Vue.observable so the
 * component's save-status indicator reacts to it directly.
 */

import Vue from 'vue';
import { log as debugLog, error as debugError } from '@/utils/debugLogger';

const COALESCE_MS = 1000;
const SAVED_FADE_MS = 1500;

/**
 * @param {object} deps
 * @param {() => Array<object>} deps.getLiveLayout  Returns the live grid items (each with
 *   id + x/y/w/h + the full widget fields needed for the PUT body).
 * @param {(payload: object, context: object) => Promise<any>} deps.writeWidget  Performs
 *   ONE captured-context layout write; resolves on success, rejects on failure.
 */
export function createLayoutSaveQueue({ getLiveLayout, writeWidget }) {
  // Reactive slice the indicator binds to. lastAcknowledgedLayout = geometry as of the
  // last successful flush (what Discard restores to).
  const state = Vue.observable({
    status: 'idle', // idle | saving | saved | error
    lastAcknowledgedLayout: [],
  });

  let coalesceTimer = null;
  let savedFadeTimer = null;
  let capturedContext = {};
  // Serialized tail: every flush chains onto this so only one runs at a time.
  let chain = Promise.resolve(true);
  // Generation token — bumped by discard()/abandonPending() so an in-flight write's
  // response is dropped instead of writing a stale coordinate back into the baseline.
  let gen = 0;

  function geometryOf(item) {
    return { id: item.id, x: item.x, y: item.y, w: item.w, h: item.h };
  }

  /** Snapshot the current layout geometry as the acknowledged baseline (edit-enter / reload). */
  function initAcknowledged(layout) {
    state.lastAcknowledgedLayout = (layout || []).map(geometryOf);
    state.status = 'idle';
    debugLog('[saveQueue] baseline captured', state.lastAcknowledgedLayout.length, 'widgets');
  }

  /** Widgets whose x/y/w/h differ from the acknowledged baseline (or are new). */
  function computeDiff() {
    const acked = new Map(state.lastAcknowledgedLayout.map(a => [a.id, a]));
    const changed = [];
    for (const item of getLiveLayout()) {
      const a = acked.get(item.id);
      if (!a || a.x !== item.x || a.y !== item.y || a.w !== item.w || a.h !== item.h) {
        changed.push(item);
      }
    }
    return changed;
  }

  function ackWidget(item) {
    const g = geometryOf(item);
    const idx = state.lastAcknowledgedLayout.findIndex(a => a.id === g.id);
    const next = state.lastAcknowledgedLayout.slice();
    if (idx > -1) next.splice(idx, 1, g);
    else next.push(g);
    state.lastAcknowledgedLayout = next;
  }

  function markSaved() {
    state.status = 'saved';
    clearTimeout(savedFadeTimer);
    savedFadeTimer = setTimeout(() => {
      if (state.status === 'saved') state.status = 'idle';
    }, SAVED_FADE_MS);
  }

  /**
   * Compute the diff and write each changed widget in order. Returns true when the layout
   * is fully persisted (clean), false when a write failed (blocked) or the flush was
   * superseded by a discard/abandon.
   */
  async function runFlush(context) {
    const diff = computeDiff();
    if (!diff.length) {
      if (state.status !== 'error') state.status = 'idle';
      return true;
    }
    const myGen = gen;
    state.status = 'saving';
    debugLog('[saveQueue] flush start —', diff.length, 'changed widget(s)');
    for (const item of diff) {
      if (myGen !== gen) {
        debugLog('[saveQueue] flush superseded — abandoning remaining writes');
        return false;
      }
      // Snapshot the geometry as sent BEFORE the await. vue-grid-layout mutates layout
      // items in place, so acking the live item after the await can record a newer
      // position this PUT never persisted; a second move mid-flight would then look
      // already-acked and be dropped. Ack exactly what this write sent.
      const sent = geometryOf(item);
      const payload = { ...item, width: item.w, height: item.h };
      try {
        await writeWidget(payload, context);
        if (myGen !== gen) return false; // response arrived after a discard — ignore it
        ackWidget(sent);
      } catch (err) {
        if (myGen !== gen) return false;
        debugError('[saveQueue] layout write failed for widget', item.id, err);
        state.status = 'error';
        return false;
      }
    }
    markSaved();
    debugLog('[saveQueue] flush complete — layout persisted');
    return true;
  }

  /** Chain a flush onto the serialized tail. Prior failures never break the chain. */
  function enqueueFlush(context) {
    chain = chain.catch(() => false).then(() => runFlush(context));
    return chain;
  }

  function cancelCoalesce() {
    if (coalesceTimer) {
      clearTimeout(coalesceTimer);
      coalesceTimer = null;
    }
  }

  /**
   * Trailing-coalesce entry point — called on every move/resize. (Re)arms the ~1s timer;
   * the LATEST captured context wins (all changes in one window share a page + facility).
   */
  function scheduleFlush(context) {
    capturedContext = context || {};
    cancelCoalesce();
    coalesceTimer = setTimeout(() => {
      coalesceTimer = null;
      enqueueFlush(capturedContext);
    }, COALESCE_MS);
    debugLog('[saveQueue] change buffered — coalesce armed');
  }

  /**
   * Exit drain (spec §5 steps 2-5): cancel the pending timer, let any in-flight write
   * settle, then run the final captured-context diff on the SAME chain. Resolves true when
   * clean, false when blocked by a failed write.
   */
  function flushForExit(context) {
    cancelCoalesce();
    const ctx = context || capturedContext;
    return enqueueFlush(ctx);
  }

  /** Re-attempt after a failed flush (Retry). */
  function retry(context) {
    return enqueueFlush(context || capturedContext);
  }

  /**
   * Discard escape hatch — drop the pending diff and reset status. Bumps the generation so
   * any in-flight write's response is ignored (it can't recall the PUT, only its effect on
   * the baseline). Returns the acknowledged geometry the caller restores locally; the
   * caller owns the actual layout restore + the grid :key bump (forced re-render).
   */
  function discard() {
    gen += 1;
    cancelCoalesce();
    state.status = 'idle';
    debugLog('[saveQueue] discard — pending diff dropped, restoring baseline');
    return state.lastAcknowledgedLayout.slice();
  }

  /**
   * Deletion primitive (plan step 14 deletion branch; wired by W5): drop the buffered +
   * pending diff and stop tracking in-flight results WITHOUT surfacing their failure, so a
   * page delete is never blocked by a save that was racing it. Does NOT enqueue a final
   * diff. Returns the in-flight tail so the caller can await it settling if it wants to.
   */
  function abandonPending() {
    gen += 1;
    cancelCoalesce();
    if (state.status !== 'saved') state.status = 'idle';
    debugLog('[saveQueue] abandonPending — diffs dropped, in-flight results ignored');
    return chain.catch(() => false);
  }

  /**
   * Cheap "are there un-persisted coordinate changes?" probe for the unload guard: true
   * when a coalesce timer is still armed, or the live layout diverges from the acknowledged
   * baseline (buffered, in-flight-but-unacked, or a failed flush). The armed-timer check
   * short-circuits so the diff only runs when the timer has already settled.
   */
  function hasPending() {
    return coalesceTimer !== null || computeDiff().length > 0;
  }

  /** Clear both timers on teardown — closes the narrow stray-write-after-destroy vector. */
  function dispose() {
    cancelCoalesce();
    clearTimeout(savedFadeTimer);
  }

  return {
    state,
    initAcknowledged,
    scheduleFlush,
    flushForExit,
    cancelCoalesce,
    retry,
    discard,
    abandonPending,
    hasPending,
    dispose,
  };
}

export default { createLayoutSaveQueue };
